import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import { Grid, AppBar, Tabs, Tab, Chip } from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import PropTypes from "prop-types";

import {
  tableContainerStyle,
  teacherManagementStyle,
  tableHeadStyle,
  tableStyleID,
  tableStyleDesc,
  tableStyleDate,
} from "./style";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import FormNotification from "components/FormNotification";
import TitleBoxComponent from "components/TitleBoxComponent";
import breakpoints from "assets/theme/base/breakpoints";
import StatusCell from "components/StatusCell";

import {
  useLazyGetScheduleNotificationsTodayQuery,
  useLazyGetTemplateDetailNotificationQuery,
} from "shared/redux/endpoints/notifications";
import AdvanceTable from "components/AdvancedTable/AdvanceTable";
import moment from "moment";

import { statusColor } from "utilities/colorUtil";
import { NotificationTarget } from "constant/NotificationType";

const EnhancedTableHead = ({ title }) => {
  return (
    <MDBox
      sx={({ breakpoints, functions: { pxToRem } }) => ({
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginRight: pxToRem(24),

        [breakpoints.down(1367)]: {
          marginRight: pxToRem(16),
        },
      })}
    >
      <MDTypography sx={(theme) => tableHeadStyle(theme)}>{title}</MDTypography>
      <MDBox sx={() => ({ display: "flex", alignItems: "center" })}>
        <ExpandLess
          sx={({ palette }) => ({ color: palette.textColors.secondary })}
        />
        <ExpandMore
          sx={({ palette }) => ({ color: palette.textColors.secondary })}
        />
      </MDBox>
    </MDBox>
  );
};

EnhancedTableHead.propTypes = {
  title: PropTypes.string.isRequired,
};

const valueTabs = [
  {
    value: 0,
    label: "Schedule noti",
    target: NotificationTarget.TEACHERS,
    recipient: "",
  },
  {
    value: 1,
    label: "Noti to NTW",
    target: NotificationTarget.ALL,
    recipient: "recipients",
  },
  {
    value: 2,
    label: "Noti to center list",
    target: NotificationTarget.CENTERS,
    recipient: "centers",
  },
  {
    value: 3,
    label: "Noti to teacher list",
    target: NotificationTarget.TEACHERS,
    recipient: "teachers",
  },
  {
    value: 4,
    label: "Noti to LXO list",
    target: NotificationTarget.LXOS,
    recipient: "LXOs",
  },
];

function NotificationManagement() {
  const [tabsOrientation, setTabsOrientation] = useState("horizontal");
  const [tabValue, setTabValue] = useState(0);
  const [dataTable, setDataTable] = useState({
    columns: [
      { Header: "ID", accessor: "id", width: 80 },
      { Header: "title", accessor: "title", width: 200 },
      {
        Header: "time push",
        accessor: "timePush",
        Cell: ({ value }) => {
          const status = value.status;
          const date = value.date;
          let result;

          if (status === "SUCCESS") {
            result = <StatusCell icon="done" color="success" status={date} />;
          } else if (status === "FAIL") {
            result = <StatusCell icon="close" color="error" status={date} />;
          } else if (status === "IN_PROGRESS") {
            result = <StatusCell icon="replay" color="dark" status={date} />;
          } else {
            result = <StatusCell icon="replay" color="dark" status={date} />;
          }

          return result;
        },
        width: 200,
      },
      { Header: `number of recipients`, accessor: "numberRecipient" },
      { Header: "Push by", accessor: "pushBy", width: 90 },
      { Header: "Status", accessor: "status", width: 90 },
    ],
    rows: [],
  });
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");
  const [errorMessage, setErrorMessage] = useState(null);

  const [
    getScheduleNotificationsToday,
    { isFetching: isFetchingScheduleNotificationsToday },
  ] = useLazyGetScheduleNotificationsTodayQuery();

  const [getTemplateDetailNotifcation, { data: dataTemplate }] =
    useLazyGetTemplateDetailNotificationQuery();

  const handleTabsOrientation = () => {
    return window.innerWidth < breakpoints.values.sm
      ? setTabsOrientation("vertical")
      : setTabsOrientation("horizontal");
  };

  useEffect(() => {
    window.addEventListener("resize", handleTabsOrientation);

    handleTabsOrientation();

    return () => window.removeEventListener("resize", handleTabsOrientation);
  }, [tabsOrientation]);

  const handleGetDataTable = (tabValue) => {
    if (isFetchingScheduleNotificationsToday) return;
    setErrorMessage(null);
    getScheduleNotificationsToday({
      sort: "created,desc",
      page: 0,
      size: 10,
      target:
        valueTabs.find((item) => item.value === tabValue).target || "TEACHERS",
    }).then((response) => {
      if (response.error) {
        setErrorMessage(
          response.error.message || "Failed to fetch notifications"
        );
        setDataTable({ ...dataTable, rows: [] });
      } else if (response.data.status) {
        const customRows = response.data.data.map((item) => {
          return {
            ...item,
            id: <MDTypography sx={tableStyleID}># {item.id}</MDTypography>,
            numberRecipient: (
              <MDTypography sx={tableStyleID}>
                {item.sendTo.length}
              </MDTypography>
            ),
            timePush: {
              date: (
                <MDTypography sx={tableStyleDate}>
                  {moment(item.scheduleTime)
                    .add(7, "hour")
                    .format("YYYY-MM-DD HH:mm:ss")}
                </MDTypography>
              ),
              status: item.status,
            },
            title: (
              <MDTypography sx={tableStyleID}>
                {item.useTemplate
                  ? `Using template: ${item.templateId}`
                  : item.titleEn}
              </MDTypography>
            ),
            pushBy: <MDTypography sx={tableStyleDesc}>Admin</MDTypography>,
            status: (
              <Chip
                label={item.status}
                sx={{
                  backgroundColor:
                    statusColor[item.status].background ||
                    statusColor.UNDEFINED.background,
                  borderColor:
                    statusColor[item.status].border ||
                    statusColor.UNDEFINED.borderColor,
                  color:
                    statusColor[item.status].color ||
                    statusColor.UNDEFINED.color,
                }}
              />
            ),
          };
        });

        setDataTable({
          ...dataTable,
          columns: dataTable.columns.map((col) =>
            col.accessor === "numberRecipient"
              ? {
                  ...col,
                  Header: `number of ${
                    valueTabs.find((item) => item.value === tabValue)
                      ?.recipient || "recipients"
                  }`,
                }
              : col
          ),
          rows: customRows,
        });
      }
    });
  };

  useEffect(() => {
    handleGetDataTable(tabValue);
    id && getTemplateDetailNotifcation({ id });
  }, [tabValue, id]);

  const handleSetTabValue = (event, newValue) => {
    handleGetDataTable(newValue);
    setTabValue(newValue);
  };

  const propertiesFormNotification = {
    dataTemplate,
    target:
      valueTabs.find((item) => item.value === tabValue).target || "TEACHERS",
    recipient:
      valueTabs.find((item) => item.value === tabValue).recipient || "teachers",
  };

  const advanceTableProps = {
    columns: dataTable.columns,
    rows: dataTable.rows,
    filters: [],
  };

  return (
    <>
      <Grid container>
        <Grid item xs={12} sm={10} xl={8}>
          <MDBox mb={3}>
            <AppBar position="static">
              <Tabs
                orientation={tabsOrientation}
                value={tabValue}
                onChange={handleSetTabValue}
                sx={{
                  padding: 0.5,
                  background: "#fff",
                  gap: "20px",
                  display: "flex",
                }}
                TabIndicatorProps={{
                  style: {
                    fontWeight: "bold",
                    color: "#fff",
                  },
                }}
              >
                {valueTabs.map((item, index) => {
                  const isActive = tabValue === index;

                  return (
                    <Tab
                      sx={{
                        fontWeight: isActive ? 700 : 400,
                        minWidth: "150px",
                      }}
                      label={item.label}
                      key={item.value}
                      TabIndicatorProps={{
                        style: {
                          minWidth: "350px",
                        },
                      }}
                    />
                  );
                })}
              </Tabs>
            </AppBar>
          </MDBox>
        </Grid>
      </Grid>

      <MDBox sx={(theme) => teacherManagementStyle(theme)}>
        <TitleBoxComponent title="Today's queue" />
        <MDBox width="100%">
          {errorMessage ? (
            <MDBox sx={{ textAlign: "center", padding: 3 }}>
              <MDTypography color="error">{errorMessage}</MDTypography>
            </MDBox>
          ) : (
            <MDBox
              data-horizontal-scroll
              sx={(theme) => tableContainerStyle(theme)}
            >
              <AdvanceTable {...advanceTableProps} />
            </MDBox>
          )}
        </MDBox>
      </MDBox>

      <MDBox sx={(theme) => teacherManagementStyle(theme)}>
        <TitleBoxComponent title="Add a notification " />
        <FormNotification {...propertiesFormNotification} />
      </MDBox>
    </>
  );
}

export { NotificationManagement };
